import React, { useEffect, useState } from "react";

import { phappsRequest } from "@/services/apiService";
import MUIDataGrid from "@/components/tables/doclink/MUIDataGrid";
import ChartCard from "@/components/data-visuals/ChartCard";

import StatBox from "@/components/data-visuals/Statbox";
import SFDateRangeSelector from "@/components/inputs/SFDateRangeSelector";
import SecondaryStatsParent from "@/components/data-visuals/SecondaryStatsParent";
import { API_PATHS } from "@/config/apiConstants";
import DocLinkLogo from "@/Images/Logos/DocLink_Logo.png";
import { DocLinkBoxTheme } from "@/config/boxThemes";
import { DOCLINK_COLORS } from "@/config/colors";
import { EventAvailable, CalendarMonth } from "@mui/icons-material";
import { Box } from "@mui/material";

const DocLinkDash = () => {
  const [data, setData] = useState({});
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [chartData, setChartData] = useState({
    TotalBookingsPerDay: [],
  });
  const [selectionModel, setSelectionModel] = useState([]);
  const [rawData, setRawData] = useState({
    TotalBookingsPerDay: [],
    RevenueOverTime: [],
  });

  useEffect(() => {
    const today = new Date();
    const fourWeeksAgo = new Date();
    fourWeeksAgo.setDate(today.getDate() - 28);

    setDateStart(fourWeeksAgo.toISOString().split("T")[0]);
    setDateEnd(today.toISOString().split("T")[0]);
  }, []);

  const calculateStats = (data, dateStart, dateEnd) => {
    const startDate = new Date(dateStart);
    const endDate = new Date(dateEnd);

    // Create a map of all days in the date range with default values
    const dateMap = new Map();
    for (
      let date = new Date(startDate);
      date <= endDate;
      date.setDate(date.getDate() + 1)
    ) {
      const dateKey = date.toISOString().split("T")[0]; // Format as YYYY-MM-DD
      dateMap.set(dateKey, { TotalBookings: 0, DailyRevenue: 0 });
    }

    // Populate the map with actual data
    (data || []).forEach((item) => {
      const bookingDate = new Date(item.BookingDate)
        .toISOString()
        .split("T")[0];
      if (dateMap.has(bookingDate)) {
        const existingData = dateMap.get(bookingDate);
        existingData.TotalBookings += parseInt(item.TotalBookings) || 0;
        existingData.DailyRevenue += parseFloat(item.DailyRevenue) || 0;
        dateMap.set(bookingDate, existingData);
      }
    });

    // Calculate total bookings and total revenue from the map
    let totalBookings = 0;
    let totalRevenue = 0;
    dateMap.forEach(({ TotalBookings, DailyRevenue }) => {
      totalBookings += TotalBookings;
      totalRevenue += DailyRevenue;
    });

    // Calculate the total number of days in the range
    const totalDays = dateMap.size;

    // Calculate the average daily appointments
    const averageDailyAppointments =
      totalDays > 0 ? (totalBookings / totalDays).toFixed(2) : 0;

    return {
      totalBookings,
      totalRevenue: totalRevenue.toFixed(2),
      averageDailyAppointments,
    };
  };

  const preprocessData = (data, dateStart, dateEnd, filter = []) => {
    const normalizedFilter = Array.isArray(filter)
      ? filter
          .filter((value) => value !== undefined && value !== null)
          .map((value) => String(value))
      : [];
    const startDate = new Date(dateStart);
    const endDate = new Date(dateEnd);
    const dateMap = new Map();

    (data || [])
      .filter((item) => {
        const chemistId = item?.ChemistID;
        const keep =
          normalizedFilter.length === 0 ||
          normalizedFilter.includes(String(chemistId));
        return keep;
      })
      .forEach((item) => {
        const dateKey = new Date(item.BookingDate).toISOString().split("T")[0];

        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, {
            BookingDate: dateKey,
            TotalScriptRequests: 0,
            TotalConsults: 0,
            TotalBookings: 0,
            DailyRevenue: 0,
            ChemistIDs: new Set(),
          });
        }

        const existingData = dateMap.get(dateKey);

        existingData.TotalScriptRequests +=
          parseInt(item.TotalScriptRequests) || 0;
        existingData.TotalConsults += parseInt(item.TotalConsults) || 0;
        existingData.TotalBookings += parseInt(item.TotalBookings) || 0;
        existingData.DailyRevenue += parseFloat(item.DailyRevenue) || 0;
        existingData.ChemistIDs.add(item.ChemistID);

        dateMap.set(dateKey, existingData);
      });

    const result = [];
    for (
      let date = new Date(startDate);
      date <= endDate;
      date.setDate(date.getDate() + 1)
    ) {
      const dateKey = date.toISOString().split("T")[0];
      result.push(
        dateMap.get(dateKey) || {
          BookingDate: dateKey,
          TotalScriptRequests: 0,
          TotalConsults: 0,
          TotalBookings: 0,
          DailyRevenue: 0,
          ChemistIDs: new Set(),
        }
      );
    }

    return result;
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const params = { DateStart: dateStart, DateEnd: dateEnd };
      const response = await phappsRequest(
        API_PATHS.DOCLINK_DASHBOARD,
        "GET",
        null,
        params
      );

      // Save raw data
      setRawData({
        TotalBookingsPerDay: response.TotalBookingsPerDay || [],
      });

      // Preprocess and save grouped data
      const processedBookings = preprocessData(
        response.TotalBookingsPerDay,
        dateStart,
        dateEnd
      );

      setData({
        ...response,
        TotalBookingsPerDay: processedBookings,
      });

      setChartData({
        TotalBookingsPerDay: processedBookings,
      });

      // Calculate and set stats immediately to prevent flash
      const statsData = calculateStats(processedBookings, dateStart, dateEnd);
      setData((prevData) => ({
        ...prevData,
        stats: statsData,
      }));
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (rawData.TotalBookingsPerDay && !isLoading) {
      const filteredBookings = preprocessData(
        rawData.TotalBookingsPerDay,
        dateStart,
        dateEnd,
        selectionModel
      );

      const updatedChartData = {
        TotalBookingsPerDay: filteredBookings,
      };

      // Calculate stats for the filtered data
      const statsData = calculateStats(filteredBookings, dateStart, dateEnd);

      setChartData(updatedChartData);
      setData((prevData) => ({
        ...prevData,
        stats: statsData,
      }));
    }
  }, [selectionModel, rawData, dateStart, dateEnd, isLoading]);

  useEffect(() => {
    if (dateStart && dateEnd) {
      fetchData();
    }
  }, [dateStart, dateEnd]);

  const handleSelectionChange = (newSelection) => {
    setSelectionModel(
      Array.isArray(newSelection)
        ? newSelection.map((value) => String(value))
        : []
    );
  };

  const totalAppointmentsOriginal = rawData.TotalBookingsPerDay.reduce(
    (sum, item) => sum + (parseInt(item.TotalBookings) || 0),
    0
  );

  const averageAppointmentsOriginal = (() => {
    const startDate = new Date(dateStart);
    const endDate = new Date(dateEnd);
    const totalDays =
      Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24) + 1) || 1;
    return (totalAppointmentsOriginal / totalDays).toFixed(2);
  })();
  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <div className="p-4 space-y-4 w-full max-w-full">
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading DocLink data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Stats Section */}
            <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="bg-gradient-to-r from-orange-dark to-orange-light shadow-lg rounded-lg p-1 flex items-center justify-center min-w-0">
                <Box
                  sx={{
                    width: "100%",
                    "& .text-black": { color: "white !important" },
                    "& .e-input-group input.e-input": {
                      color: "white !important",
                    },
                    "& .e-input-group.e-control-wrapper input.e-input": {
                      color: "white !important",
                    },
                    "& .e-input-group-icon": { color: "white !important" },
                  }}
                >
                  <SFDateRangeSelector
                    value={[new Date(dateStart), new Date(dateEnd)]}
                    onChange={(range) => {
                      setDateStart(range[0]?.toISOString().split("T")[0]);
                      setDateEnd(range[1]?.toISOString().split("T")[0]);
                    }}
                  />
                </Box>
              </div>

              <div className="min-w-0">
                <StatBox
                  theme={DocLinkBoxTheme}
                  logo={DocLinkLogo}
                  title="Appointments"
                  value={data.stats?.totalBookings || 0}
                  valueFormatter={(v) => Number(v || 0).toLocaleString()}
                  showAcrossGroup={selectionModel.length > 0}
                  acrossGroupValue={totalAppointmentsOriginal.toLocaleString()}
                  Icon={EventAvailable}
                />
              </div>
              <div className="min-w-0">
                <StatBox
                  theme={DocLinkBoxTheme}
                  logo={DocLinkLogo}
                  title="Avg Daily Appts"
                  value={Number(data.stats?.averageDailyAppointments || 0)}
                  valueFormatter={(v) => Number(v || 0).toFixed(2)}
                  showAcrossGroup={selectionModel.length > 0}
                  acrossGroupValue={averageAppointmentsOriginal}
                  Icon={CalendarMonth}
                />
              </div>

              {/* Secondary Stats Cards */}
              <div className="min-w-0">
                <SecondaryStatsParent
                  topPharmacy={data?.TopPharmacy}
                  topStaff={data?.TopStaffMember}
                />
              </div>
            </section>

            {/* Chart */}
            <div className="w-full overflow-x-auto">
              <ChartCard
                title="Bookings and Revenue"
                data={chartData}
                primaryXAxis={{
                  valueType: "DateTime",
                  title: "Booking Date",
                  labelFormat: "dd/MM",
                  intervalType: "Days",
                }}
                primaryYAxis={{
                  title: "Total Bookings",
                  labelFormat: "{value}",
                }}
                borderClass="border-4 border-purple-light"
                series={[
                  {
                    dataSource: chartData.TotalBookingsPerDay || [],
                    xName: "BookingDate",
                    yName: "TotalConsults",
                    type: "StackingColumn",
                    name: "Consults",
                    fill: "#E35437",
                    columnSpacing: 0.4,
                    cornerRadius: { topLeft: 4, topRight: 4 },
                  },
                  {
                    dataSource: chartData.TotalBookingsPerDay || [],
                    xName: "BookingDate",
                    yName: "TotalScriptRequests",
                    type: "StackingColumn",
                    name: "Script Requests",
                    fill: "#8d679f",
                    columnSpacing: 0.4,
                    cornerRadius: { topLeft: 4, topRight: 4 },
                  },
                ]}
                pieData={[
                  {
                    x: "Consults",
                    y: chartData.TotalBookingsPerDay.reduce(
                      (acc, curr) => acc + curr.TotalConsults,
                      0
                    ),
                    color: "#E35437",
                  },
                  {
                    x: "Script Requests",
                    y: chartData.TotalBookingsPerDay.reduce(
                      (acc, curr) => acc + curr.TotalScriptRequests,
                      0
                    ),
                    color: "#a78bfa",
                  },
                ]}
                pieTitle="Appointment Types"
              />
            </div>

            {/* Data Table */}
            <div className="w-full overflow-x-auto">
              <MUIDataGrid
                data={data.OverviewTable?.map((row) => ({
                  id: String(row?.ChemistID),
                  ...row,
                }))}
                onSelectionChange={handleSelectionChange}
                selectionModel={selectionModel}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DocLinkDash;
